import { gql, useQuery } from '@apollo/client';

export interface DspBillPaymentVoucherType {
    id: string;
    fileName: string;
    fileSize: number;
    contentType: string;
    downloadUrl?: string | null;
    uploadedByName?: string | null;
    uploadedAt?: number | null;
}

export interface DspBillPaymentFinanceInfo {
    vendorCountry?: string | null;
    federalTaxClassification?: string | null;
    taxIdType?: string | null;
    taxIdNumber?: string | null;
    trackFor1099?: boolean | null;
    bankName?: string | null;
    accountType?: string | null;
    accountNo?: string | null;
    routingNo?: string | null;
    vendorAddress?: string | null;
    masked: boolean;
}

export interface DspBillPaymentInfoType {
    billId: string;
    financeInfo?: DspBillPaymentFinanceInfo | null;
    expectedPaymentDate?: number | null;
    paidAt?: number | null;
    vouchers: DspBillPaymentVoucherType[];
    /** DSP 是否已提交当前有效 Invoice；false 时财务侧不展示「确认 Invoice」按钮 */
    invoiceSubmitted?: boolean | null;
    /** 已提交的 Invoice 是否为 DSP 自制件；未提交时后端恒返回 false */
    invoiceSelfMade?: boolean | null;
}

const PAYMENT_INFO_FIELDS = gql`
    fragment DspBillPaymentInfoFields on DspBillPaymentInfoType {
        billId
        financeInfo {
            vendorCountry
            federalTaxClassification
            taxIdType
            taxIdNumber
            trackFor1099
            bankName
            accountType
            accountNo
            routingNo
            vendorAddress
            masked
        }
        expectedPaymentDate
        paidAt
        vouchers {
            id
            fileName
            fileSize
            contentType
            downloadUrl
            uploadedByName
            uploadedAt
        }
        invoiceSubmitted
        invoiceSelfMade
    }
`;

/** 总部 */
export const GET_DSP_BILL_PAYMENT_INFO = gql`
    query GetDspBillPaymentInfo($billId: ID!) {
        paymentInfo: getDspBillPaymentInfo(billId: $billId) {
            ...DspBillPaymentInfoFields
        }
    }
    ${PAYMENT_INFO_FIELDS}
`;

/** HUB */
export const GET_HUB_DSP_BILL_PAYMENT_INFO = gql`
    query GetHubDspBillPaymentInfo($hubId: Long!, $billId: ID!) {
        paymentInfo: getHubDspBillPaymentInfo(hubId: $hubId, billId: $billId) {
            ...DspBillPaymentInfoFields
        }
    }
    ${PAYMENT_INFO_FIELDS}
`;

/** DSP Owner */
export const GET_MY_DSP_BILL_PAYMENT_INFO = gql`
    query GetMyDspBillPaymentInfo($billId: ID!) {
        paymentInfo: getMyDspBillPaymentInfo(billId: $billId) {
            ...DspBillPaymentInfoFields
        }
    }
    ${PAYMENT_INFO_FIELDS}
`;

interface PaymentInfoResponse {
    paymentInfo: DspBillPaymentInfoType;
}

export const useDspBillPaymentInfo = (billId?: string) =>
    useQuery<PaymentInfoResponse, { billId: string }>(GET_DSP_BILL_PAYMENT_INFO, {
        variables: { billId: billId ?? '' },
        skip: !billId,
        fetchPolicy: 'network-only'
    });

export const useHubDspBillPaymentInfo = (hubId?: number, billId?: string) =>
    useQuery<PaymentInfoResponse, { hubId: number; billId: string }>(GET_HUB_DSP_BILL_PAYMENT_INFO, {
        variables: { hubId: hubId ?? 0, billId: billId ?? '' },
        skip: !billId || !hubId,
        fetchPolicy: 'network-only'
    });

export const useMyDspBillPaymentInfo = (billId?: string) =>
    useQuery<PaymentInfoResponse, { billId: string }>(GET_MY_DSP_BILL_PAYMENT_INFO, {
        variables: { billId: billId ?? '' },
        skip: !billId,
        fetchPolicy: 'network-only'
    });
